import React, {memo,  useMemo,useRef,useEffect,useLayoutEffect,useCallback, useState, ReactNode} from "react";
import {useMsal} from "@azure/msal-react";
import {loginRequest, pca, apiRequest} from "../../components/sso/authConfig";
import {useDispatch} from "react-redux";
// import {AppState} from "../../../root/RootReducer";
import {AuthComplete} from "src/user/UserActions";
import {UserLogin} from "../../user/User";
import {motion, AnimatePresence} from "framer-motion";
import {InteractionStatus,  InteractionRequiredAuthError,EventType,EventMessage} from "@azure/msal-browser";
import styles from "./LoginPage.module.css";
import iconMicroSoft from "./microsoft-icon.svg";
import UserApi from "src/user/UserApi";
import userService from "src/utils/UserService";
import {useSnackbar} from "notistack";
import {useSearchParams, useNavigate} from "react-router-dom";
import {validateEmail} from "./helper";
import {CircularProgress} from "@mui/material";
export function useEffectEvent<T extends (...args: any[]) => any>(
    handler: T
): T {
    const handlerRef = useRef<T | null>(null);

    // 保证在浏览器 paint 之前同步更新
    useLayoutEffect(() => {
        handlerRef.current = handler;
    });

    return useCallback(((...args) => {
        return handlerRef.current?.(...args);
    }) as T, []);
}
function LoginPage() {
    const {enqueueSnackbar} = useSnackbar();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // const {userInfo} = useSelector((state: AppState) => ({
    //     userInfo: state.user.userInfo
    // }));
    const dispatch = useDispatch();
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");

    const handledRef = useRef<Set<string>>(new Set());   // 二重実行ガード
    // apiRequest はレンダーごとに新しいオブジェクトになる可能性があるため、useMemo で固定する
    const stableApiRequest = useMemo(() => ({...apiRequest}), []);
    const hasDispatchedRef = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const loginFinish = (userInfo:any) => {
        userService.email = userInfo.email;
        dispatch(AuthComplete(userInfo));
        const redirectUrl = searchParams.get("redirect_uri");
        navigate(redirectUrl ? decodeURIComponent(redirectUrl) : "/");
    };
    const handleLogin = async () => {
        if (isLoading) {
            return;
        }
        if (!email || !password) {
            enqueueSnackbar("メールアドレスまたはワンタイムパスコードが不正です。", {variant: "error"});
            return;
        }
        if(!validateEmail(email)) {
            enqueueSnackbar("正しいメールアドレスを入力してください。", {variant: "error"});
            return;
        }
        setIsLoading(true);
        try {
            const userInfo = await UserApi.Auth(email, password);
            console.log("login success:", userInfo);
            loginFinish(userInfo);
        } catch(e) {
            console.log("catch error", e);
            enqueueSnackbar("メールアドレスまたはワンタイムパスコードが不正です。", {variant: "error"});
        } finally {
            setIsLoading(false);
        }
    };

    const {instance, inProgress} = useMsal();
    //     useEffect(() => {
    //         if (userInfo && userInfo.email) {
    //             window.location.href = "/homepage";
    //         }
    //     // eslint-disable-next-line
    //   }, [userInfo]);

    // ログイン成功ハンドラの登録は「一度だけ」、必ず解除
    const pacEventCallback = useEffectEvent( async (event:EventMessage) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            if (hasDispatchedRef.current) return;                     // Redux dispatch済みなら抜ける
            const account = (event.payload as any).account;
            const key = (event.payload as any)?.account?.homeAccountId; // 一意キー
            if (key && handledRef.current.has(key)) return;             // 既処理なら抜ける
            if (key) handledRef.current.add(key);
            // フラグ設定
            hasDispatchedRef.current = true;
            setIsLoading(true);
            try {
                pca.setActiveAccount(account);
                const response = await  pca.acquireTokenSilent({
                    ...stableApiRequest,
                    account
                });
                // // 初めてSSO登録する場合、1回のみ実施
                const userInfo = await UserApi.verifyToken(response.accessToken, {
                    email: account.username,
                    fullName: account.name,
                    isNew: true,
                    profileImageUrl: "",
                    organization: "",
                    localAccountId: account.localAccountId ?? ""
                });
                loginFinish(userInfo);
            } finally {
                hasDispatchedRef.current = false;
                setIsLoading(false);
            }
            console.log("✅ ログイン成功！リダイレクトからの戻り結果：", event.payload);
        } else if (event.eventType === EventType.LOGIN_FAILURE && event.error) {
            console.error("❌ ログイン失敗：", event.error);
        } else if (event.eventType === EventType.HANDLE_REDIRECT_END) {
            console.log("📨 handleRedirectPromise() は MsalProvider により自動的に処理されました。");
        }
    });
    useEffect(() => {
        const pcaCallbackId = pca.addEventCallback(pacEventCallback);
        return () => {
            setIsLoading(false);
            hasDispatchedRef.current = false;
            pca.removeEventCallback(pcaCallbackId);
        };
    },[pacEventCallback]);

    /**
   * 「SSOログイン」ボタンをクリックした時の処理
   * 1. すでにアカウントが存在する場合 → まず acquireTokenSilent() でトークンを静かに取得
   * 2. トークンが無効または期限切れの場合 → loginRedirect() で再ログイン
   * 3. まだアカウントが存在しない場合 → loginRedirect() で初回ログイン
   */
    const handleSSOLogin = async () => {
        if (isLoading) {
            return;
        }
        // 多重クリック防止
        if (inProgress !== InteractionStatus.None) return;

        // 現在のアクティブアカウント、または保存されている最初のアカウントを取得
        const account =
      instance.getActiveAccount() || instance.getAllAccounts()[0] || null;
        if (!account) {
        // アカウントが存在しない場合は、新規ログインを実施
            console.log("🟡 アカウント未検出 → 新規ログインを開始");
            setIsLoading(true);
            await instance.loginRedirect({
                ...loginRequest
            }).catch(error => {
                console.error("Login redirect failed:", error);
            });
            setIsLoading(false);
            return;
        }
        try {
            setIsLoading(true);
            // トークンを静かに取得（ポップアップやリダイレクトなし）
            const tokenResponse = await pca.acquireTokenSilent({
                ...stableApiRequest,
                account
            });

            const userLogin: UserLogin = {
                email: account.username,
                fullName: account.name,
                isNew: true,
                profileImageUrl: "",
                organization: "",
                localAccountId: account.localAccountId ?? ""
            };
            console.log("🔑 acquireTokenSilent successful:", tokenResponse);
            const userInfo = await  UserApi.verifyToken(tokenResponse.accessToken, userLogin);
            loginFinish(userInfo);
            setIsLoading(false);
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                setIsLoading(true);
                await instance.loginRedirect({
                    ...loginRequest
                }).catch(error => {
                    console.error("Login redirect failed:", error);
                });
                setIsLoading(false);
            } else {
                console.error("❌ トークン取得エラー：", error);
            }
        }
    };

    const loadingText = useCallback((text:string | ReactNode,color = "white") => {
        return isLoading ? (
            <CircularProgress size={16} style={{
                color
            }}
            />
        ) : text;
    },[isLoading]);
    return (
        <AnimatePresence mode="wait">
            <motion.div
                className={styles.loginContainer}
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                exit={{opacity: 0}}
                transition={{duration: 0.3}}
            >
                <div className={styles.loginCard}>
                    <h1 className={styles.title}>ログイン</h1>
                    <div className={styles.inputGroup}>
                        <label htmlFor="username">メールアドレス</label>
                        <input
                            id="username"
                            name="username"
                            type="email"
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label htmlFor="password">パスワード</label>
                        <input
                            id="password"
                            name="password"
                            type="password"
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>
                    <button type="button" className={styles.loginBtn} onClick={handleLogin}>
                        {loadingText("ログイン")}
                    </button>
                    <div className={styles.otherLoginGroup}>
                        <label>その他登録方式</label>
                        <div><hr className={styles.hrBlue}/></div>
                        <div>
                            <button type="button" className={styles.ssoBtn} onClick={handleSSOLogin}>
                                {loadingText(<>
                                    <img  width={32} height={32} src={iconMicroSoft} style={{
                                        marginRight:"8px"
                                    }}
                                    />
                                    <div>
                                    Sign in with Microsoft
                                    </div>
                                </>,"#333")}

                                
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}

export default memo(LoginPage);
